import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  GripVertical,
  Check,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { Input, Select, Textarea } from '../components/ui/Input.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import type { QuestionnaireType, QuestionnaireStatus } from './QuestionnairesPage.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

type QuestionType = 'single_choice' | 'multiple_choice' | 'text' | 'rating';

interface QuestionOption {
  id: string;
  text: string;
  isCorrect?: boolean;
}

interface Question {
  id: string;
  text: string;
  type: QuestionType;
  order: number;
  options: QuestionOption[];
  ratingMin?: number;
  ratingMax?: number;
  ratingMinLabel?: string;
  ratingMaxLabel?: string;
  // Results
  votesByOption?: Record<string, number>;
}

interface QuizSettings {
  timeLimit?: number | null;
  passingScore?: number | null;
  shuffleQuestions?: boolean;
}

interface PollSettings {
  allowAnonymous?: boolean;
  showResults?: boolean;
}

interface QuestionnaireDetail {
  id: string;
  type: QuestionnaireType;
  status: QuestionnaireStatus;
  title: string;
  slug: string;
  description: string | null;
  programId: string | null;
  startAt: string | null;
  endAt: string | null;
  completionPoints: number | null;
  maxResponses: number | null;
  settings: QuizSettings & PollSettings;
  questions: Question[];
  responseCount: number;
  leaderboard?: Array<{ customerName: string; score: number; timeSeconds: number }>;
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function typeLabel(type: QuestionnaireType) {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

function typeVariant(type: QuestionnaireType) {
  if (type === 'poll') return 'info' as const;
  if (type === 'quiz') return 'purple' as const;
  return 'warning' as const;
}

function statusVariant(status: QuestionnaireStatus) {
  if (status === 'published') return 'success' as const;
  if (status === 'closed') return 'default' as const;
  return 'warning' as const;
}

function newOption(): QuestionOption {
  return { id: crypto.randomUUID(), text: '', isCorrect: false };
}

function newQuestion(order: number): Question {
  return {
    id: crypto.randomUUID(),
    text: '',
    type: 'single_choice',
    order,
    options: [newOption(), newOption()],
  };
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden mb-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50 transition-colors"
      >
        <span className="text-sm font-semibold text-zinc-900">{title}</span>
        {open ? (
          <ChevronUp size={16} className="text-zinc-400" />
        ) : (
          <ChevronDown size={16} className="text-zinc-400" />
        )}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

// ─── Question Card ────────────────────────────────────────────────────────────

function QuestionCard({
  question,
  index,
  total,
  isQuiz,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: {
  question: Question;
  index: number;
  total: number;
  isQuiz: boolean;
  onChange: (q: Question) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const isChoice = question.type === 'single_choice' || question.type === 'multiple_choice';

  function updateOption(optId: string, text: string) {
    onChange({
      ...question,
      options: question.options.map((o) => (o.id === optId ? { ...o, text } : o)),
    });
  }

  function toggleCorrect(optId: string) {
    onChange({
      ...question,
      options: question.options.map((o) =>
        o.id === optId ? { ...o, isCorrect: !o.isCorrect } : o,
      ),
    });
  }

  function addOption() {
    onChange({ ...question, options: [...question.options, newOption()] });
  }

  function removeOption(optId: string) {
    onChange({ ...question, options: question.options.filter((o) => o.id !== optId) });
  }

  return (
    <div className="border border-zinc-200 rounded-lg bg-zinc-50 p-4">
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex flex-col gap-0.5 pt-1">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <ChevronUp size={14} />
          </button>
          <GripVertical size={14} className="text-zinc-300" />
          <button
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="text-zinc-300 hover:text-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <ChevronDown size={14} />
          </button>
        </div>

        <div className="flex-1 min-w-0 space-y-2.5">
          <div className="flex items-start gap-2">
            <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded bg-zinc-200 text-zinc-500 text-xs font-semibold mt-1.5">
              {index + 1}
            </span>
            <div className="flex-1">
              <input
                value={question.text}
                onChange={(e) => onChange({ ...question, text: e.target.value })}
                placeholder="Enter question text…"
                className="w-full bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-3 h-9"
              />
            </div>
          </div>

          <select
            value={question.type}
            onChange={(e) =>
              onChange({
                ...question,
                type: e.target.value as QuestionType,
                options:
                  e.target.value === 'text' || e.target.value === 'rating'
                    ? []
                    : question.options.length
                      ? question.options
                      : [newOption(), newOption()],
              })
            }
            className="h-8 px-2.5 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
          >
            <option value="single_choice">Single choice</option>
            <option value="multiple_choice">Multiple choice</option>
            <option value="text">Open text</option>
            <option value="rating">Rating</option>
          </select>

          {/* Choice options */}
          {isChoice && (
            <div className="space-y-1.5 pl-1">
              {question.options.map((opt) => (
                <div key={opt.id} className="flex items-center gap-2">
                  {isQuiz && (
                    <button
                      onClick={() => toggleCorrect(opt.id)}
                      title={opt.isCorrect ? 'Mark incorrect' : 'Mark correct'}
                      className={
                        opt.isCorrect
                          ? 'flex-shrink-0 w-5 h-5 rounded flex items-center justify-center bg-emerald-500 text-white'
                          : 'flex-shrink-0 w-5 h-5 rounded border border-zinc-300 bg-white hover:border-emerald-400 transition-colors'
                      }
                    >
                      {opt.isCorrect && <Check size={11} strokeWidth={3} />}
                    </button>
                  )}
                  {!isQuiz && (
                    <span className="flex-shrink-0 w-3.5 h-3.5 rounded-full border border-zinc-300 bg-white" />
                  )}
                  <input
                    value={opt.text}
                    onChange={(e) => updateOption(opt.id, e.target.value)}
                    placeholder={`Option ${question.options.indexOf(opt) + 1}`}
                    className="flex-1 bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-2.5 h-7"
                  />
                  <button
                    onClick={() => removeOption(opt.id)}
                    disabled={question.options.length <= 2}
                    className="text-zinc-300 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={addOption}
                className="text-xs text-zinc-400 hover:text-zinc-700 flex items-center gap-1 mt-1 transition-colors"
              >
                <Plus size={11} />
                Add option
              </button>
            </div>
          )}

          {/* Rating settings */}
          {question.type === 'rating' && (
            <div className="grid grid-cols-2 gap-2 pl-1">
              <input
                type="number"
                value={question.ratingMin ?? 1}
                min={0}
                onChange={(e) =>
                  onChange({ ...question, ratingMin: Number(e.target.value) })
                }
                placeholder="Min"
                className="bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-2.5 h-7"
              />
              <input
                type="number"
                value={question.ratingMax ?? 5}
                min={1}
                onChange={(e) =>
                  onChange({ ...question, ratingMax: Number(e.target.value) })
                }
                placeholder="Max"
                className="bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-2.5 h-7"
              />
              <input
                value={question.ratingMinLabel ?? ''}
                onChange={(e) => onChange({ ...question, ratingMinLabel: e.target.value })}
                placeholder="Min label (e.g. Poor)"
                className="bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-2.5 h-7"
              />
              <input
                value={question.ratingMaxLabel ?? ''}
                onChange={(e) => onChange({ ...question, ratingMaxLabel: e.target.value })}
                placeholder="Max label (e.g. Excellent)"
                className="bg-white border border-zinc-200 rounded-md text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent px-2.5 h-7"
              />
            </div>
          )}
        </div>

        <button
          onClick={onDelete}
          className="flex-shrink-0 p-1.5 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors mt-0.5"
          title="Delete question"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ─── Results Section ──────────────────────────────────────────────────────────

function ResultsSection({ questionnaire }: { questionnaire: QuestionnaireDetail }) {
  if (questionnaire.type === 'poll') {
    return (
      <div className="space-y-4">
        <p className="text-sm text-zinc-500">
          Total responses: <strong className="text-zinc-900">{questionnaire.responseCount}</strong>
        </p>
        {questionnaire.questions.map((q) => {
          const votes = q.votesByOption ?? {};
          const total = Object.values(votes).reduce((a, b) => a + b, 0) || 1;
          return (
            <div key={q.id}>
              <p className="text-sm font-medium text-zinc-700 mb-2">{q.text}</p>
              <div className="space-y-1.5">
                {q.options.map((opt) => {
                  const count = votes[opt.id] ?? 0;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={opt.id}>
                      <div className="flex items-center justify-between text-xs text-zinc-500 mb-0.5">
                        <span>{opt.text || `Option`}</span>
                        <span>{count} ({pct}%)</span>
                      </div>
                      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-sky-400 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (questionnaire.type === 'quiz') {
    const board = questionnaire.leaderboard ?? [];
    return (
      <div>
        <p className="text-sm text-zinc-500 mb-3">
          Total responses: <strong className="text-zinc-900">{questionnaire.responseCount}</strong>
        </p>
        {board.length === 0 ? (
          <p className="text-sm text-zinc-400">No quiz responses yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="text-left py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  #
                </th>
                <th className="text-left py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="text-left py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Score
                </th>
                <th className="text-left py-2 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {board.map((entry, i) => (
                <tr key={i}>
                  <td className="py-2 pr-3 text-zinc-400 text-xs">{i + 1}</td>
                  <td className="py-2 font-medium text-zinc-800">{entry.customerName}</td>
                  <td className="py-2 tabular-nums text-zinc-700">{entry.score}</td>
                  <td className="py-2 tabular-nums text-zinc-500 text-xs">
                    {entry.timeSeconds}s
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  // survey
  return (
    <div>
      <p className="text-sm text-zinc-500 mb-3">
        Total responses:{' '}
        <strong className="text-zinc-900">{questionnaire.responseCount}</strong>
      </p>
      <a
        href={`/questionnaires/${questionnaire.id}/responses`}
        className="text-sm font-medium text-zinc-900 underline underline-offset-2 hover:text-zinc-600"
      >
        View all responses →
      </a>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function QuestionnaireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Local editable state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [programId, setProgramId] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [completionPoints, setCompletionPoints] = useState('');
  const [maxResponses, setMaxResponses] = useState('');
  const [settings, setSettings] = useState<QuizSettings & PollSettings>({});
  const [questions, setQuestions] = useState<Question[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data: q, isLoading, isError } = useQuery({
    queryKey: ['questionnaire', id],
    queryFn: () =>
      api.get<QuestionnaireDetail>(`/api/v1/questionnaires/${id}`).then((r) => r.data),
    enabled: !!id,
    staleTime: 30_000,
    // Seed local state on first load
    select: (data) => {
      return data;
    },
  });

  // Sync local state when data first loads (or changes from server)
  const [initialised, setInitialised] = useState(false);
  if (q && !initialised) {
    setTitle(q.title);
    setSlug(q.slug);
    setDescription(q.description ?? '');
    setProgramId(q.programId ?? '');
    setStartAt(q.startAt ? q.startAt.slice(0, 10) : '');
    setEndAt(q.endAt ? q.endAt.slice(0, 10) : '');
    setCompletionPoints(q.completionPoints != null ? String(q.completionPoints) : '');
    setMaxResponses(q.maxResponses != null ? String(q.maxResponses) : '');
    setSettings(q.settings ?? {});
    setQuestions(q.questions ?? []);
    setInitialised(true);
  }

  // ── Save mutation ──────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () =>
      api
        .patch<QuestionnaireDetail>(`/api/v1/questionnaires/${id}`, {
          title: title.trim(),
          slug: slug.trim(),
          description: description.trim() || null,
          programId: programId.trim() || null,
          startAt: startAt || null,
          endAt: endAt || null,
          completionPoints: completionPoints ? Number(completionPoints) : null,
          maxResponses: maxResponses ? Number(maxResponses) : null,
          settings,
          questions: questions.map((q, i) => ({ ...q, order: i })),
        })
        .then((r) => r.data),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      toast.success('Changes saved');
    },
    onError: (err) => toast.error(extractError(err, 'Failed to save')),
  });

  // ── Publish / Close mutations ──────────────────────────────────────────────
  const publishMutation = useMutation({
    mutationFn: (action: 'publish' | 'close') =>
      api
        .post<QuestionnaireDetail>(`/api/v1/questionnaires/${id}/${action}`, {})
        .then((r) => r.data),
    onSuccess: (_, action) => {
      queryClient.invalidateQueries({ queryKey: ['questionnaire', id] });
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
      toast.success(action === 'publish' ? 'Published' : 'Closed');
    },
    onError: (err) => toast.error(extractError(err, 'Action failed')),
  });

  // ── Question helpers ───────────────────────────────────────────────────────
  function updateQuestion(updated: Question) {
    setQuestions((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    setDirty(true);
  }

  function deleteQuestion(qid: string) {
    setQuestions((prev) => prev.filter((q) => q.id !== qid));
    setDirty(true);
  }

  function addQuestion() {
    setQuestions((prev) => [...prev, newQuestion(prev.length)]);
    setDirty(true);
  }

  function moveQuestion(index: number, dir: 'up' | 'down') {
    const next = [...questions];
    const swap = dir === 'up' ? index - 1 : index + 1;
    [next[index], next[swap]] = [next[swap], next[index]];
    setQuestions(next);
    setDirty(true);
  }

  function markDirty() {
    setDirty(true);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-zinc-400">
        Loading…
      </div>
    );
  }

  if (isError || !q) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-red-500">
        Failed to load questionnaire.
      </div>
    );
  }

  const isQuiz = q.type === 'quiz';
  const isPoll = q.type === 'poll';
  const isPublished = q.status === 'published';
  const isClosed = q.status === 'closed';

  return (
    <div className="max-w-3xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-start gap-3 mb-6">
        <button
          onClick={() => navigate('/questionnaires')}
          className="mt-1 p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors flex-shrink-0"
          title="Back"
        >
          <ArrowLeft size={16} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={typeVariant(q.type)}>{typeLabel(q.type)}</Badge>
            <Badge variant={statusVariant(q.status)} dot>
              {q.status}
            </Badge>
          </div>
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); markDirty(); }}
            className="w-full text-xl font-semibold text-zinc-900 bg-transparent border-0 outline-none focus:bg-zinc-50 focus:ring-2 focus:ring-zinc-200 rounded-md px-1 -mx-1 transition-colors"
            placeholder="Questionnaire title"
          />
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 mt-1">
          {dirty && (
            <Button
              size="sm"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save
            </Button>
          )}
          {q.status === 'draft' && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              loading={publishMutation.isPending && publishMutation.variables === 'publish'}
              onClick={() => publishMutation.mutate('publish')}
            >
              Publish
            </Button>
          )}
          {isPublished && (
            <Button
              size="sm"
              variant="ghost"
              loading={publishMutation.isPending && publishMutation.variables === 'close'}
              onClick={() => publishMutation.mutate('close')}
            >
              Close
            </Button>
          )}
          {isClosed && (
            <span className="text-xs text-zinc-400 px-2">Closed</span>
          )}
        </div>
      </div>

      {/* ── Section 1: Settings ── */}
      <Section title="Settings">
        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); markDirty(); }}
            />
            <Input
              label="Slug"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); markDirty(); }}
            />
          </div>

          <Textarea
            label="Description"
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            rows={2}
            placeholder="Optional description…"
          />

          <Input
            label="Program ID (optional)"
            value={programId}
            onChange={(e) => { setProgramId(e.target.value); markDirty(); }}
            placeholder="Link to a loyalty program"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start date"
              type="date"
              value={startAt}
              onChange={(e) => { setStartAt(e.target.value); markDirty(); }}
            />
            <Input
              label="End date"
              type="date"
              value={endAt}
              onChange={(e) => { setEndAt(e.target.value); markDirty(); }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Completion points"
              type="number"
              min={0}
              value={completionPoints}
              onChange={(e) => { setCompletionPoints(e.target.value); markDirty(); }}
              placeholder="0"
            />
            <Input
              label="Max responses"
              type="number"
              min={0}
              value={maxResponses}
              onChange={(e) => { setMaxResponses(e.target.value); markDirty(); }}
              placeholder="Unlimited"
            />
          </div>

          {/* Type-specific settings */}
          {isQuiz && (
            <div className="border-t border-zinc-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Quiz settings
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Time limit (seconds)"
                  type="number"
                  min={0}
                  value={settings.timeLimit ?? ''}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, timeLimit: e.target.value ? Number(e.target.value) : null }));
                    markDirty();
                  }}
                  placeholder="No limit"
                />
                <Input
                  label="Passing score (%)"
                  type="number"
                  min={0}
                  max={100}
                  value={settings.passingScore ?? ''}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, passingScore: e.target.value ? Number(e.target.value) : null }));
                    markDirty();
                  }}
                  placeholder="e.g. 70"
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={settings.shuffleQuestions ?? false}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, shuffleQuestions: e.target.checked }));
                    markDirty();
                  }}
                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
                />
                <span className="text-sm text-zinc-700">Shuffle question order</span>
              </label>
            </div>
          )}

          {isPoll && (
            <div className="border-t border-zinc-100 pt-4 space-y-3">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                Poll settings
              </p>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={settings.allowAnonymous ?? false}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, allowAnonymous: e.target.checked }));
                    markDirty();
                  }}
                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
                />
                <span className="text-sm text-zinc-700">Allow anonymous responses</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={settings.showResults ?? false}
                  onChange={(e) => {
                    setSettings((s) => ({ ...s, showResults: e.target.checked }));
                    markDirty();
                  }}
                  className="rounded border-zinc-300 text-zinc-900 focus:ring-zinc-500"
                />
                <span className="text-sm text-zinc-700">Show live results to participants</span>
              </label>
            </div>
          )}
        </div>
      </Section>

      {/* ── Section 2: Questions ── */}
      <Section title={`Questions (${questions.length})`}>
        <div className="space-y-3 mt-3">
          {questions.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-4">
              No questions yet. Add your first question below.
            </p>
          )}

          {questions.map((question, index) => (
            <QuestionCard
              key={question.id}
              question={question}
              index={index}
              total={questions.length}
              isQuiz={isQuiz}
              onChange={updateQuestion}
              onDelete={() => deleteQuestion(question.id)}
              onMoveUp={() => moveQuestion(index, 'up')}
              onMoveDown={() => moveQuestion(index, 'down')}
            />
          ))}

          <button
            onClick={addQuestion}
            className="flex items-center gap-2 w-full px-4 py-3 border border-dashed border-zinc-300 rounded-lg text-sm text-zinc-500 hover:border-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            <Plus size={15} />
            Add question
          </button>
        </div>
      </Section>

      {/* ── Section 3: Results (read-only, published only) ── */}
      {(isPublished || isClosed) && (
        <Section title="Results" defaultOpen={false}>
          <div className="mt-3">
            <ResultsSection questionnaire={q} />
          </div>
        </Section>
      )}

      {/* ── Floating save bar ── */}
      {dirty && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="flex items-center gap-3 bg-zinc-900 text-white px-4 py-2.5 rounded-xl shadow-lg text-sm">
            <span className="text-zinc-300">Unsaved changes</span>
            <Button
              size="sm"
              className="bg-white text-zinc-900 hover:bg-zinc-100"
              loading={saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
